import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../../state/zustand-store';
import {
  doApiCheck,
  getAllResourceCalendars,
  getAllRoles,
  getBulkImportTypes,
  getDomains,
  saveBulkImport,
  UserKeys,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ModuleList } from '../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { BulkImportModel, BulkImportType } from '../../../../../types';
import dayjs from 'dayjs';
import StepBasicInformation from './step-one';
import StepReview from './step-review';
import StepSources from './step-sources';
import StepSpreadsheet from './step-spreadsheet';
import StepSpreadsheetTemplate from './step-spreadsheet-template';
import StepSpreadsheetTemplateOld from './step-spreadsheet-template-old';
import StepFolder from './step-folder';
import StepPathSelection from './step-path-selection';
import StepPathSelectionOld from './step-path-selection-old';
import StepSelectColumns from './step-select-columns';
import StepVacationResponder from './step-vacation-responder';
import StepGroupConfig from './step-group-config';
import StepConfiguration from './step-select-resource';
import StepAdditonalOptions from './step-additional-options';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import SimpleBreadcrumbsPage from '../../../../shared/simple-breadcrumbs-page';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../../shared/unsaved-changes-dialog';
import SimpleFormPage, {
  getSchemaKeys,
} from '../../../../shared/simple-form-page';
import SharedFormStepper, {
  StepConfig,
} from '../../../../shared/shared-form-stepper';
import SimpleFormPageNew from '../../../../shared/simple-form-page-new';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../../../shared/pages-config';
import FormSkeleton from './form-skeleton';

import { z, ZodObject, ZodRawShape } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DateRangeZodType,
  SpreadSheetColumnSchema,
} from '../../../../shared/shared-schema';
import { getBulkOpsSchema } from './bulk-ops-schema';
import { isAbleToSeeUIElement } from '../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';

export interface BulkOpsForm extends BulkImportModel {
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
}

const VacationModelSchema = z.object({
  enableAutoReply: z.boolean(),
  responseSubject: z.string().nullable(),
  responseBodyPlainText: z.string().nullable().optional(),
  responseBodyHtml: z.string().nullable(),
  restrictToContacts: z.boolean(),
  restrictToDomain: z.boolean(),
  startTime: z.number().nullable(),
  endTime: z.number().nullable(),
});

const MetaDataSchema = z.object({
  includeNestedGroups: z.array(z.string()).nullable(),
  reportHasRan: z.boolean(),
  createdDate: z.number(),
  lastRan: z.number().catch(0),
  lastExportUpdate: z.number().nullable(),
  createdBy: z.string(),
  setAsProcessed: z.boolean(),
  createGroups: z.boolean(),
});

const BasicInformationSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    bulkImport: z.string(),
    notifyEmails: z.array(z.string()).nullable(),
  })
  .superRefine((data, ctx) => {
    if (!data.name || data.name.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Name is required.',
        path: ['name'],
      });
    }
    if (!data.bulkImport || data.bulkImport.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Bulk Import Type is required.',
        path: ['bulkImport'],
      });
    }
  });

const ConfigurationSchema = z
  .object({
    resourceIds: z.array(z.string()).nullable(),
    startDate: z.number({ error: 'Must be a valid start date.' }),
    endDate: z.number({ error: 'Must be a valid end date.' }),
  })
  .superRefine((data, ctx) => {
    if (data.resourceIds !== null && data.resourceIds.length <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Resource is required.',
        path: ['resourceIds'],
      });
    }
    if (data.startDate > data.endDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['startDate'],
        message: 'Start date must be before end date.',
      });
    }
  });

const SelectColumnSchema = z.object({
  columns: z.array(SpreadSheetColumnSchema).nullable(),
});

const SelectPathSchema = z
  .object({
    path: z.enum(['guideMe', 'advanced']).nullable(),
    spreadsheetId: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      data.path === 'advanced' &&
      (!data.spreadsheetId || data.spreadsheetId.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Please provide a Google Sheet to use for this bulk operation.',
        path: ['spreadsheetId'],
      });
    }
    if (
      !data.path &&
      data.spreadsheetId != null &&
      data.spreadsheetId.trim() === ''
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please select a spreadsheet before proceeding.',
        path: ['spreadsheetId'],
      });
    }
  });

export const SourceSchema = z
  .object({
    sourceType: z
      .enum([
        'All_Users',
        'All_Groups',
        'User',
        'Group',
        'OrgUnit',
        'gPanel_Role',
        'Mobile_Device',
        'Domain',
      ])
      .nullable(),
    sources: z.array(z.string()).nullable(),
    allSource: z.boolean(),
    existingLabelFilter: z.string().optional().nullable(),
    visibilityFilter: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      !data.allSource &&
      data.sourceType &&
      // The path selection resets sources to null, which also means none picked.
      (data.sources?.length ?? 0) <= 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Target is required.',
        path: ['sources'],
      });
    }
  });

const VacationResponderSchema = z
  .object({
    vacationResponderData: VacationModelSchema.nullable(),
    dateRange: DateRangeZodType.optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.vacationResponderData &&
      (!data.vacationResponderData.responseSubject ||
        data.vacationResponderData.responseSubject.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Subject is required.',
        path: ['vacationResponderData', 'responseSubject'],
      });
    }

    if (
      data.vacationResponderData &&
      (!data.vacationResponderData.responseBodyHtml ||
        data.vacationResponderData.responseBodyHtml.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Body cannot be blank.',
        path: ['vacationResponderData', 'responseBodyHtml'],
      });
    }

    if (
      !data.dateRange ||
      !data.dateRange[0]?.isValid() ||
      !data.dateRange[1]?.isValid()
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Both start and end dates are required.',
        path: ['dateRange'],
      });
    } else if (
      data.dateRange &&
      data.dateRange[0] &&
      data.dateRange[1] &&
      data.dateRange[1].isBefore(data.dateRange[0])
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be after the start date.',
        path: ['dateRange'],
      });
    }
  });

export const BulkOpsSchema = BasicInformationSchema.and(ConfigurationSchema)
  .and(SelectPathSchema)
  .and(SelectColumnSchema)
  .and(SourceSchema)
  .and(VacationResponderSchema)
  .and(MetaDataSchema);

export type BulkOpsFormValues = z.infer<typeof BulkOpsSchema>;

export default function BulkOpFormPage() {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [spreadSheetName, setSpreadSheetName] = useState('');
  const [groupDeletionChecked, setGroupDeletionChecked] = useState(false);
  const [showDeletionError, setShowDeletionError] = useState(false);
  // const [templateError, setTemplateError] = useState(false);
  const [selectedBulkImportType, setSelectedBulkImportType] =
    useState<BulkImportType | null>(null);
  const navigate = useNavigate();
  const { loggedInUser, toolsRoles, changeLogRoles, features } =
    useUserSettings();
  const isNewBulkOpUi = isFeatureEnabled('newBulkOpUi', features);
  const [stepCompleted, setStepCompleted] = useState(() => [
    false,
    false,
    false,
    false,
    false,
  ]);

  const { data: bulkImportTypes, isLoading: typesLoading } = useQuery({
    queryKey: [UserKeys.BulkOpsTypes],
    queryFn: () => getBulkImportTypes(),
    meta: {
      errorMessage:
        'There was an issue loading bulk import data. Please refresh and try again.',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    trigger,
    reset,
    getValues,
    setValue,
    watch,
    clearErrors,
  } = useForm<BulkOpsFormValues>({
    resolver: zodResolver(getBulkOpsSchema(bulkImportTypes)),
    defaultValues: {
      createdBy: loggedInUser,
      createdDate: dayjs(new Date()).valueOf(),
      columns: null,
      name: '',
      spreadsheetId: '',
      description: '',
      bulkImport: '',
      sourceType: null,
      allSource: false,
      reportHasRan: false,
      createGroups: false,
      sources: null,
      lastExportUpdate: null,
      setAsProcessed: false,
      startDate: 0,
      endDate: 0,
      notifyEmails: null,
      path: null,
      vacationResponderData: null,
      dateRange: [
        dayjs().startOf('day').add(1, 'day'),
        dayjs().endOf('day').add(2, 'day'),
      ] as DateRange<dayjs.Dayjs>,
      includeNestedGroups: null,
      resourceIds: null,
      existingLabelFilter: '',
      visibilityFilter: '',
    },
  });

  const handleCancel = () => {
    navigate(`/${pagesConfig.bulkOps.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const { data: calendars, isLoading: calendarsLoading } = useQuery({
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: () => getAllResourceCalendars(),
  });

  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: [UserKeys.Domains],
    queryFn: () => getDomains(),
  });

  const { data: roles } = useQuery({
    queryKey: [UserKeys.Roles],
    queryFn: () => getAllRoles(),
  });

  const bulkImportTypeWithTemplate = [
    'bulkAssignAuLicenses',
    'calendarEventsImport',
  ];
  const path = watch('path');
  const sourceType = watch('sourceType');
  const [selectedOption, setSelectedOption] = useState<
    'template' | 'existing' | null
  >('template');

  const cloudIdentityBulkOpTypes = ['groupMemberSettingsUpdate'];

  const isCloudIdentityBulkOpTypesSelected =
    selectedBulkImportType?.name !== '' &&
    selectedBulkImportType?.name !== undefined &&
    cloudIdentityBulkOpTypes.includes(selectedBulkImportType?.name);

  const {
    data: accessGranted,
    isLoading: checkingApiAccess,
    isFetching: fetchingAccess,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'cloudIdentity'],
    queryFn: () => doApiCheck('cloudIdentity', null),
    enabled: isCloudIdentityBulkOpTypesSelected,
    staleTime: 0, // Set staleTime to 0 to prevent it from being cached
  });

  const driveLabelsBulkOpTypes = ['labelDriveDocs'];

  const isDriveLabelsBulkOpTypesSelected =
    selectedBulkImportType?.name !== '' &&
    selectedBulkImportType?.name !== undefined &&
    driveLabelsBulkOpTypes.includes(selectedBulkImportType?.name);

  const {
    data: driveLabelsAccessGranted,
    isLoading: checkingDriveLabelsAccess,
    isFetching: fetchingDriveLabelsAccess,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'driveLabels'],
    queryFn: () => doApiCheck('driveLabels', null),
    enabled: isDriveLabelsBulkOpTypesSelected,
    staleTime: 0, // Set staleTime to 0 to prevent it from being cached
  });

  let steps: StepConfig[] = [
    { label: 'Basic Information', schema: BasicInformationSchema },
    { label: 'Review' },
  ];

  if (
    selectedBulkImportType?.name === 'personalContactRemoval' ||
    selectedBulkImportType?.name === 'groupRemoval' ||
    selectedBulkImportType?.name === 'groupDeletion' ||
    selectedBulkImportType?.name === 'clearUsersFromGroup' ||
    selectedBulkImportType?.name === 'forceSignOut' ||
    selectedBulkImportType?.name === 'forcePasswordReset'
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Sources', schema: SourceSchema },
      { label: 'Review' },
    ];
  } else if (selectedBulkImportType?.name === 'sharedContactRemoval') {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Review' },
    ];
  } else if (selectedBulkImportType?.name === 'bulkUploadPics') {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Choose Folder', schema: SelectPathSchema },
      { label: 'Review' },
    ];
  } else if (
    (selectedBulkImportType?.name === 'calendarPermissionSettings' &&
      path === 'guideMe') ||
    (selectedBulkImportType?.name === 'signatureTemplateBlackListStatus' &&
      path === 'guideMe') ||
    (selectedBulkImportType?.name === 'userImport' && path === 'guideMe') ||
    (selectedBulkImportType?.name === 'gPanelRoleImport' &&
      path === 'guideMe') ||
    (selectedBulkImportType?.name === 'groupMemberSettingsUpdate' &&
      path === 'guideMe') ||
    (selectedBulkImportType?.name === 'labelDriveDocs' && path === 'guideMe') ||
    (selectedBulkImportType?.name === 'removeUsersShares' &&
      path === 'guideMe') ||
    (selectedBulkImportType?.name === 'removeSharesFromUser' &&
      path === 'guideMe')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Configuration', schema: SelectPathSchema },
      { label: 'Select Columns', schema: SelectColumnSchema },
      { label: 'Sources', schema: SourceSchema },
      { label: 'Review' },
    ];
  } else if (
    (selectedBulkImportType?.name === 'calendarPermissionSettings' &&
      path === 'advanced') ||
    (selectedBulkImportType?.name === 'signatureTemplateBlackListStatus' &&
      path === 'advanced') ||
    (selectedBulkImportType?.name === 'userImport' && path === 'advanced') ||
    (selectedBulkImportType?.name === 'gPanelRoleImport' &&
      path === 'advanced') ||
    (selectedBulkImportType?.name === 'groupMemberSettingsUpdate' &&
      path === 'advanced') ||
    (selectedBulkImportType?.name === 'labelDriveDocs' &&
      path === 'advanced') ||
    (selectedBulkImportType?.name === 'removeUsersShares' &&
      path === 'advanced') ||
    (selectedBulkImportType?.name === 'removeSharesFromUser' &&
      path === 'advanced')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Configuration', schema: SelectPathSchema },
      { label: 'Review' },
    ];
  } else if (
    selectedBulkImportType?.name === 'sharedContactImport' &&
    path === 'guideMe'
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Configuration', schema: SelectPathSchema },
      { label: 'Select Columns', schema: SelectColumnSchema },
      { label: 'Sources', schema: SourceSchema },
      { label: 'Review' },
    ];
  } else if (
    selectedBulkImportType?.name === 'sharedContactImport' &&
    path === 'advanced'
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Configuration', schema: SelectPathSchema },
      { label: 'Sources', schema: SourceSchema },
      { label: 'Review' },
    ];
  } else if (
    selectedBulkImportType?.name === 'groupImport' &&
    path === 'guideMe' &&
    sourceType === 'Group'
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Configuration', schema: SelectPathSchema },
      { label: 'Select Columns', schema: SelectColumnSchema },
      { label: 'Sources', schema: SourceSchema },
      { label: 'Group Configuration' },
      { label: 'Review' },
    ];
  } else if (
    selectedBulkImportType?.name === 'groupImport' &&
    path === 'guideMe' &&
    sourceType === 'Domain'
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Configuration', schema: SelectPathSchema },
      { label: 'Select Columns', schema: SelectColumnSchema },
      { label: 'Sources', schema: SourceSchema },
      { label: 'Additional Options' },
      { label: 'Review' },
    ];
  } else if (
    selectedBulkImportType?.name === 'groupImport' &&
    path === 'advanced'
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Configuration', schema: SelectPathSchema },
      { label: 'Additional Options' },
      { label: 'Review' },
    ];
  } else if (selectedBulkImportType?.name === 'usersVacationResponder') {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Sources', schema: SourceSchema },
      { label: 'Set Vacation Responder', schema: VacationResponderSchema },
      { label: 'Review' },
    ];
  } else if (selectedBulkImportType?.name === 'bulkAssignAuLicenses') {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Choose Spreadsheet', schema: SelectPathSchema },
      { label: 'Review' },
    ];
  } else if (selectedBulkImportType?.name === 'removeCalendarRoom') {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Configuration', schema: ConfigurationSchema },
      { label: 'Review' },
    ];
  } else if (
    selectedBulkImportType?.usesSpreadsheet ||
    selectedBulkImportType?.usesFolder
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Choose Spreadsheet', schema: SelectPathSchema },
      { label: 'Review' },
    ];
  }

  const hasSpreadsheetTemplate =
    selectedBulkImportType?.name != null &&
    bulkImportTypeWithTemplate.includes(selectedBulkImportType.name);
  if (isNewBulkOpUi) {
    steps = steps.map((step) =>
      step.label === 'Choose Spreadsheet'
        ? { ...step, label: 'Select Spreadsheet' }
        : step
    );
  }
  const isSpreadsheetStep =
    steps[activeStep]?.label === 'Choose Spreadsheet' ||
    steps[activeStep]?.label === 'Select Spreadsheet';

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<BulkImportModel>) => {
      return saveBulkImport(data, changeLogMessage);
    },
    onSuccess: (returnData) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Bulk Operation added successfully and is now running!',
      });

      queryClient.invalidateQueries({ queryKey: [UserKeys.BulkOps] });

      if (returnData.spreadsheetId && returnData.spreadsheetId.length > 0) {
        window.open(
          `https://docs.google.com/a/${
            loggedInUser.split('@')[1]
          }/spreadsheet/ccc?key=${returnData.spreadsheetId}#gid=0`,
          '_blank',
          'noopener, noreferrer'
        );
      }

      setChangeLogMessage('');
      setSpreadSheetName('');
      setSelectedBulkImportType(null);
      setGroupDeletionChecked(false);
      setShowDeletionError(false);
      setActiveStep(0);
      reset();
      bypass();
      navigate(`/${pagesConfig.bulkOps.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding this bulk operation. Please refresh and try again.',
      });
    },
    onSettled: (_returnData, _error, data) => {
      createEvent(
        analytics.import_export.bulk_operations.create_bulk_operation,
        {
          module: ModuleList.modules.importExport.name,
          section: ModuleList.modules.importExport.sections.bulkOperations,
          bulkOperationType: data.bulkImport,
          path: data.path,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const handleNext = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (activeStep === steps.length - 1) {
      //
    } else {
      let isValid = false;
      if (
        steps[activeStep]?.label === 'Basic Information' &&
        !groupDeletionChecked &&
        selectedBulkImportType?.name === 'groupDeletion'
      ) {
        isValid = false;
        setShowDeletionError(true);
      } else {
        setShowDeletionError(false);
        const fields = steps[activeStep]?.schema
          ? getSchemaKeys(steps[activeStep]?.schema as ZodObject<ZodRawShape>)
          : [];
        isValid = await trigger(fields as never);
      }
      if (isValid) {
        setActiveStep((prevState) => prevState + 1);
        setStepCompleted((prevState) => {
          const newCompleted = [...prevState];
          newCompleted[activeStep] = true;
          return newCompleted;
        });
      }
    }
  };

  function transformDataForMutation(data: BulkOpsFormValues) {
    const updatedBulkOp = {
      ...data,
      vacationResponderData: data.vacationResponderData
        ? {
            enableAutoReply:
              data.vacationResponderData?.enableAutoReply || false,
            responseSubject:
              data.vacationResponderData?.responseSubject || null,
            responseBodyPlainText:
              data.vacationResponderData?.responseBodyPlainText || null,
            responseBodyHtml:
              data.vacationResponderData?.responseBodyHtml || null,
            restrictToContacts:
              data.vacationResponderData?.restrictToContacts || false,
            restrictToDomain:
              data.vacationResponderData?.restrictToDomain || false,
            startTime: data.dateRange![0]!.valueOf(),
            endTime: data.dateRange![1]!.valueOf(),
          }
        : null,
    };
    delete updatedBulkOp.dateRange;
    return updatedBulkOp;
  }

  const onSubmit: SubmitHandler<BulkOpsFormValues> = (data) => {
    if (
      selectedBulkImportType &&
      isAbleToSeeUIElement(changeLogRoles, selectedBulkImportType.role)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const updatedBulkOp = transformDataForMutation(data);
      mutate(updatedBulkOp);
    }
  };

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    errors,
    trigger,
    stepCompleted,
    setStepCompleted,
  };

  const selectBulkOp = getValues();

  const isLoading = typesLoading || calendarsLoading || domainsLoading;

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  const formPageProps = {
    title: pagesConfig.createBulkOps.name,
    ...formStepperProps,
    content: (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {(isLoading || !steps[activeStep]) && <FormSkeleton />}
        {!isLoading && steps[activeStep]?.label === 'Basic Information' && (
          <StepBasicInformation
            control={control}
            setValue={setValue}
            errors={errors}
            bulkImportTypes={bulkImportTypes || []}
            setSelectedBulkImportType={setSelectedBulkImportType}
            selectedBulkImportType={selectedBulkImportType}
            toolsRoles={toolsRoles}
            setSpreadSheetName={setSpreadSheetName}
            groupDeletionChecked={groupDeletionChecked}
            setGroupDeletionChecked={setGroupDeletionChecked}
            showDeletionError={showDeletionError}
            setShowDeletionError={setShowDeletionError}
            apiAccessGranted={accessGranted || false}
            checkingApiAccess={checkingApiAccess || fetchingAccess}
            cloudIdentityBulkOpTypes={cloudIdentityBulkOpTypes}
            driveLabelsAccessGranted={driveLabelsAccessGranted || false}
            checkingDriveLabelsAccess={
              checkingDriveLabelsAccess || fetchingDriveLabelsAccess
            }
            driveLabelsBulkOpTypes={driveLabelsBulkOpTypes}
            watch={watch}
            clearErrors={clearErrors}
            setStepCompleted={setStepCompleted}
            isNewUi={isNewBulkOpUi}
          />
        )}
        {!isLoading &&
          steps[activeStep]?.label === 'Configuration' &&
          !path && (
            <StepConfiguration
              control={control}
              watch={watch}
              setValue={setValue}
              errors={errors}
              calendars={calendars || []}
              isNewUi={isNewBulkOpUi}
            />
          )}

        {!isLoading &&
          steps[activeStep]?.label === 'Configuration' &&
          path &&
          (isNewBulkOpUi ? (
            <StepPathSelection
              setValue={setValue}
              control={control}
              selectedBulkImportType={selectedBulkImportType}
              setSpreadSheetName={setSpreadSheetName}
              watch={watch}
              spreadSheetName={spreadSheetName}
              errors={errors}
              onContinue={handleNext}
            />
          ) : (
            <StepPathSelectionOld
              setValue={setValue}
              control={control}
              selectedBulkImportType={selectedBulkImportType}
              setSpreadSheetName={setSpreadSheetName}
              watch={watch}
              spreadSheetName={spreadSheetName}
              errors={errors}
            />
          ))}
        {!isLoading && steps[activeStep]?.label === 'Sources' && (
          <StepSources
            control={control}
            setValue={setValue}
            watch={watch}
            errors={errors}
            trigger={trigger}
            selectedBulkImportType={selectedBulkImportType}
            domains={domains || []}
            roles={roles || []}
            clearErrors={clearErrors}
            isNewUi={isNewBulkOpUi}
          />
        )}
        {!isLoading &&
          isSpreadsheetStep &&
          hasSpreadsheetTemplate &&
          (isNewBulkOpUi ? (
            <StepSpreadsheetTemplate
              setValue={setValue}
              watch={watch}
              spreadSheetName={spreadSheetName}
              setSpreadSheetName={setSpreadSheetName}
              selectedBulkImportType={selectedBulkImportType}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              errors={errors}
              onContinue={handleNext}
            />
          ) : (
            <StepSpreadsheetTemplateOld
              setValue={setValue}
              watch={watch}
              spreadSheetName={spreadSheetName}
              setSpreadSheetName={setSpreadSheetName}
              selectedBulkImportType={selectedBulkImportType}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              errors={errors}
            />
          ))}
        {!isLoading &&
          isSpreadsheetStep &&
          selectedBulkImportType?.name != null &&
          !hasSpreadsheetTemplate && (
            <StepSpreadsheet
              control={control}
              errors={errors}
              setValue={setValue}
              spreadSheetName={spreadSheetName}
              setSpreadSheetName={setSpreadSheetName}
              selectedBulkImportType={selectedBulkImportType}
              isNewUi={isNewBulkOpUi}
            />
          )}
        {!isLoading && steps[activeStep]?.label === 'Choose Folder' && (
          <StepFolder
            setValue={setValue}
            control={control}
            watch={watch}
            spreadSheetName={spreadSheetName}
            setSpreadSheetName={setSpreadSheetName}
            errors={errors}
            isNewUi={isNewBulkOpUi}
          />
        )}
        {!isLoading &&
          steps[activeStep]?.label === 'Set Vacation Responder' && (
            <StepVacationResponder
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
            />
          )}
        {!isLoading && steps[activeStep]?.label === 'Group Configuration' && (
          <StepGroupConfig
            control={control}
            setValue={setValue}
            watch={watch}
            isNewUi={isNewBulkOpUi}
          />
        )}
        {!isLoading && steps[activeStep]?.label === 'Additional Options' && (
          <StepAdditonalOptions control={control} />
        )}
        {!isLoading && steps[activeStep]?.label === 'Select Columns' && (
          <StepSelectColumns
            control={control}
            watch={watch}
            setValue={setValue}
            selectedBulkImportType={selectedBulkImportType}
            errors={errors}
            apiAccessGranted={accessGranted || false}
            cloudIdentityBulkOpTypes={cloudIdentityBulkOpTypes}
            isNewUi={isNewBulkOpUi}
          />
        )}
        {!isLoading && steps[activeStep]?.label === 'Review' && (
          <StepReview
            selectedBulkOp={selectBulkOp}
            bulkImportTypes={bulkImportTypes ?? []}
            spreadSheetName={spreadSheetName}
            isNewUi={isNewBulkOpUi}
          />
        )}
      </Box>
    ),
    handleNext,
    handleSubmit: handleSubmit(onSubmit),
    handleDiscard: handleCancel,
    isLoadingSubmit:
      isValidating ||
      isSubmitting ||
      isPending ||
      checkingApiAccess ||
      fetchingAccess ||
      checkingDriveLabelsAccess ||
      fetchingDriveLabelsAccess,
    isDisableSubmit:
      isValidating ||
      isSubmitting ||
      isPending ||
      checkingApiAccess ||
      fetchingAccess ||
      checkingDriveLabelsAccess ||
      fetchingDriveLabelsAccess ||
      (isDriveLabelsBulkOpTypesSelected &&
        !driveLabelsAccessGranted &&
        !checkingDriveLabelsAccess &&
        !fetchingDriveLabelsAccess),
  };

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.importExport.name },
        { label: pagesConfig.bulkOps.name, path: pagesConfig.bulkOps.link },
        ...(isNewBulkOpUi ? [{ label: pagesConfig.createBulkOps.name }] : []),
      ]}
    >
      {isNewBulkOpUi ? (
        <SimpleFormPageNew
          {...formPageProps}
          hideNext={
            // Continue lives inside the option cards on these steps.
            (steps[activeStep]?.label === 'Configuration' && !!path) ||
            (isSpreadsheetStep && hasSpreadsheetTemplate)
          }
        />
      ) : (
        <SimpleFormPage
          {...formPageProps}
          stepper={<SharedFormStepper {...formStepperProps} />}
        />
      )}

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const updatedBulkOp = transformDataForMutation(getValues());
          mutate(updatedBulkOp);
        }}
        mutateLoading={isPending}
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Bulk Operation"
        pageMode="create"
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
    </SimpleBreadcrumbsPage>
  );
}
